<script>

import {axios} from '../../mixins';
import MapBoxHeatMap from "../Geography/MapBoxHeatMap.vue";
import PurchaseStatusChangeChart from "./PurchaseStatusChangeChart.vue";

export default {

  name: 'CustomerDetails',

  props: {
    customerId: {
      required: true,
      type: Number,
    },

    customerTypes: {
      required: true,
      type: Object,
      default: function () {
        return {};
      },
    },

    outcomeTypes: {
      required: true,
      type: Object,
      default: function () {
        return {};
      },
    },

    purchaseStatuses: {
      required: true,
      type: Object,
      default: function () {
        return {};
      },
    },

    range: {
      required: true,
      type: Object,
      default: [2000, 2030],
    },

    mapboxToken: {
      type: String,
      required: true,
    },

    esriToken: {
      type: String,
      required: true,
    },

    year: {
      type: String,
      required: true,
    },

    exportEnabled: {
      type: Boolean,
      required: false,
      default: false,
    }
  },

  components: {MapBoxHeatMap, PurchaseStatusChangeChart},

  mixins: [axios],

  data() {
    return {

      customer: {},
      filter: {
        year: null,
        outcome_type_id: null,
        purchase_status: null,
        purchase_template: null,
      },
      metrics: {
        compounds: {},
        heatMapData: null,
        acres: 0,
        contractCount: 0,
        fieldCount: 0,
      },

      purchaseTemplates: [],

      purchases: [],
      allPurchasesVerified: 0,
      listTotalSize: 0,

      outcomeTypesWithAcres: [],

      loading: true,
      loadingList: true,

      orgName: null,
    };
  },

  computed: {
    years() {
      let arr = [];
      for (let i = this.range.first; i <= this.range.last; i++) {
        arr.push(i);
      }
      return arr;
    },
  },

  created() {
    this.filter.year = this.year
    // Messy since outcomeTypes is an object, not an array
    for (const type in this.outcomeTypes) {
      this.outcomeTypesWithAcres[type] = this.outcomeTypes[type];
    }
    this.outcomeTypesWithAcres[0] = 'Acres';
    this.getData()
  },

  methods: {
    getData() {
      this.loading = true;
      let route = '/customer/' + this.customerId + '/data?' + this.urlParams(this.filter) + '&purchaseTemplates=' + JSON.stringify(this.purchaseTemplates);
      this.get(route).then((response) => {
        if (response.data.metrics) {
          this.metrics.compounds = response.data.metrics.compounds
        }

        this.customer = response.data.customer
        this.orgName = response.data.orgName
        this.purchaseTemplates = response.data.purchaseTemplates

        this.loading = false

        this.getPurchasesList()
        this.getCounts()
        this.getHeatMapData()
        this.getAcres()

        this.allPurchasesVerified = this.allFieldsVerified(this.purchases)

        this.$refs.statusChart.getData(this.filter.year);
      }).catch((err) => {
        console.log(err)
      })
    },
    getPurchasesList() {
      this.loadingList = true

      this.get('/customer/' + this.customerId + '/purchase-list?' + this.urlParams(this.filter)).then((response) => {
        this.purchases = response.data.purchases.list
        this.listTotalSize = response.data.purchases.totalSize
        this.loadingList = false
      }).catch((err) => {
          console.log(err)
      })
    },
    getCounts() {
      this.get('/customer/' + this.customerId + '/counts?' + this.urlParams(this.filter)).then((response) => {
        this.metrics.contractCount = response.data.contractCount
        this.metrics.fieldCount = response.data.fieldCount
      }).catch((err) => {
          console.log(err)
      })
    },
    getHeatMapData() {
      this.$nextTick(function () {
        this.$refs.heatMap.loading = true;
        this.get('/customer/' + this.customerId + '/heat-map?' + this.urlParams(this.filter)).then((response) => {
          this.metrics.heatMapData = response.data.heatMapData
          this.$refs.heatMap.loading = false;
        }).catch((err) => {
          console.log(err)
          this.$refs.heatMap.loading = false;
        })
      })
    },
    getAcres() {
      this.get('/customer/' + this.customerId + '/acres?' + this.urlParams(this.filter)).then((response) => {
        this.metrics.acres = response.data.acres
      }).catch((err) => {
        console.log(err)
      })
    },
    isMetricActive(outcomeTypeId) {
      let value = true
      if (this.filter.outcome_type_id && this.filter.outcome_type_id !== outcomeTypeId) {
        value = false
      }

      return value
    },
    getAmountForSelectedPurchaseType(outcomeTypeId) {
      if (this.filter.purchase_status) {
        return this.metrics.compounds[this.filter.purchase_status][2][outcomeTypeId]
      }

      let sum = 0
      for (const property in this.purchaseStatuses) {
        if (this.metrics.compounds[property]) {
          sum += this.metrics.compounds[property][2][outcomeTypeId]
        }
      }

      return sum
    },
    isPurchaseActive(purchase) {
      if (this.filter.outcome_type_id && parseInt(this.filter.outcome_type_id) !== purchase.outcome_type_id) {
        return false
      }

      if (this.filter.year && this.filter.year !== purchase.year) {
        return false
      }

      return !(this.filter.purchase_status && parseInt(this.filter.purchase_status) !== purchase.status);
    },
    urlParams(myData) {
      let out = [];
      for (let key in myData) {
        if (myData.hasOwnProperty(key)) {
          if (myData[key] !== null) {
            out.push(key + '=' + encodeURIComponent(myData[key]));
          }
        }
      }
      return out.join('&');
    },
    roundToTwo(value) {
      return parseFloat(value).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    allFieldsVerified(purchases) {
      let value = false;
      let hasUnverified = false;
      for (const [purchase, data] of Object.entries(purchases)) {
        if (data.year === this.filter.year){
          if (!data.verified) {
            hasUnverified = true;
          } else {
            value = true;
          }
        }
      }
      if (value && !hasUnverified) {
        return 'All Fields Verified'
      } else if (value && hasUnverified) {
        return 'Some Fields Verified'
      } else {
        return 'No Fields Verified'
      }
    }
  },
};

</script>

<template>
  <div class="light-mode w-2/3">
    <div class="flex justify-between">
      <div class="text-3xl text-center font-semibold p-2 rounded-t-md tab-shadow bg-primary">{{ customer ? customer.name : '' }} {{
          filter.year ?
              filter.year + ' ' : ''
        }} {{ this.orgName }} Registry
      </div>
    </div>
    <div v-show="this.loading" class="text-center text-2xl font-bold w-full mb-4 bg-primary rounded-tr rounded-b p-6 tile-shadow">Loading...</div>
    <div class="w-full mb-4 bg-primary rounded-tr-md p-6 tile-shadow" v-show="!this.loading">
      <!--  FILTER SELECTS  -->
      <div class="w-full mb-4">
        <div class="pt-2">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-2 my-auto w-full">
            <label hidden for="year">Year</label>
            <select name="year"
                    id="year"
                    v-model="filter.year"
                    @change="getData"
                    class="bg-tertiary italic border-b border-grey-light w-full py-05 px-3 py-2 rounded-md leading-tight focus:outline-none focus:ring">
              <option :value="null">Year</option>
              <option v-for="year in years" :value="year">{{ year }}</option>
            </select>

            <label hidden for="year">Outcome Type</label>
            <select name="outcome_type_id"
                    id="outcome_type_id"
                    v-model="filter.outcome_type_id"
                    @change="getData"
                    class="bg-tertiary italic border-b border-grey-light w-full py-05 px-3 py-2 rounded-md leading-tight focus:outline-none focus:ring">
              <option :value="null">Outcome Type</option>
              <option v-for="(outcomeType,outcome_type_id) in outcomeTypes" :value="outcome_type_id">{{ outcomeType }}
              </option>
            </select>

            <label hidden for="year">Purchase Status</label>
            <select name="purchases_status"
                    id="purchases_status"
                    v-model="filter.purchase_status"
                    @change="getData"
                    class="bg-tertiary italic border-b border-grey-light w-full py-05 px-3 py-2 rounded-md leading-tight focus:outline-none focus:ring">
              <option :value="null">Purchases Status</option>
              <option v-for="(purchaseStatus,purchases_status) in purchaseStatuses" :value="purchases_status">{{
                  purchaseStatus
                }}
              </option>
            </select>
            <label hidden for="year">Project</label>
            <select name="purchase_template"
                    id="purchase_template"
                    v-model="filter.purchase_template"
                    @change="getData"
                    class="bg-tertiary italic border-b border-grey-light w-full py-05 px-3 py-2 rounded-md leading-tight focus:outline-none focus:ring">
              <option :value="null">Projects</option>
              <option v-for="(purchaseTemplate,purchase_template) in purchaseTemplates" :value="purchase_template">{{
                  purchaseTemplate
                }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!--  METRICS / MAP  -->
      <div class="grid grid-cols-1 md:grid-cols-2  gap-4 w-full mb-4">
        <div class="grid grid-cols-2 gap-2 order-2 md:order-1">

          <!-- OUTCOME AMOUNT METRICS -->
          <template v-for="(outcomeType,outcomeTypeId) in outcomeTypes">
            <template v-for="(customerType) in customerTypes">
              <div :class="`flex flex-col bg-tertiary rounded-lg ` + (isMetricActive(outcomeTypeId) ? `` : `opacity-25`)">
                <div class="bg-secondary rounded-t-lg px-2 py-1 font-semibold">
                  {{ outcomeType }} Removed {{ outcomeType === 'CO2e' ? ' & Reduced' : ''}}
                </div>
                <div class="flex h-full">
                  <div class="text-3xl m-auto p-2">
                    {{ roundToTwo(getAmountForSelectedPurchaseType(outcomeTypeId)) }}
                    {{ 'CO2e' === outcomeTypes[outcomeTypeId] ? 'MT' : 'lbs' }}
                  </div>
                </div>
              </div>
            </template>
          </template>

          <!-- CONTRACT COUNT METRIC -->
          <div class="flex flex-col bg-tertiary rounded-lg">
            <div class="bg-secondary rounded-t-lg px-2 py-1 font-semibold">
              Contract Count
            </div>
            <div class="flex h-full">
              <div class="text-3xl m-auto p-2">
                {{ this.metrics.contractCount ? this.metrics.contractCount : '0' }}
              </div>
            </div>
          </div>

          <!-- FIELD COUNT METRIC -->
          <div class="flex flex-col bg-tertiary rounded-lg">
            <div class="bg-secondary rounded-t-lg px-2 py-1 font-semibold">
              Field Count
            </div>
            <div class="flex h-full">
              <div class="text-3xl m-auto p-2">
                {{ this.metrics.fieldCount ? this.metrics.fieldCount : '0' }}
              </div>
            </div>
          </div>

          <!-- ACRES COUNT METRIC -->
          <div class="flex flex-col bg-tertiary rounded-lg">
            <div class="bg-secondary rounded-t-lg px-2 py-1 font-semibold">
              Acres Count
            </div>
            <div class="flex h-full">
              <div class="text-3xl m-auto p-2">
                {{ roundToTwo(this.metrics.acres) }} Acres
              </div>
            </div>
          </div>

          <!-- ACRES PER FIELD COUNT METRIC -->
          <div class="flex flex-col bg-tertiary rounded-lg">
            <div class="bg-secondary rounded-t-lg px-2 py-1 font-semibold">
              Average Field Size
            </div>
            <div class="flex h-full">
              <div class="text-3xl m-auto p-2">
                {{ roundToTwo(this.metrics.acres && this.metrics.fieldCount ? this.metrics.acres / this.metrics.fieldCount : '0') }} Acres
              </div>
            </div>
          </div>

          <!-- ACRES PER CONTRACT COUNT METRIC -->
          <div class="flex flex-col bg-tertiary rounded-lg">
            <div class="bg-secondary rounded-t-lg px-2 py-1 font-semibold">
              Average Contract Size
            </div>
            <div class="flex h-full">
              <div class="text-3xl m-auto p-2 text-center">
                {{ roundToTwo(this.metrics.acres && this.metrics.fieldCount ? this.metrics.acres / this.metrics.contractCount : '0') }} Acres
              </div>
            </div>
          </div>

        </div>
          <div class="order-1 md:order-2" id="map" v-if="!this.loading">
            <MapBoxHeatMap
                :source="metrics.heatMapData"
                :draw="false"
                :token="mapboxToken"
                :render="true"
                :popups="true"
                :map-id="'map'"
                ref="heatMap"
            ></MapBoxHeatMap>
          </div>
      </div>

      <!-- DETAIL LIST  -->
      <div class="w-full bg-secondary rounded-md p-4">
        <div class="flex flex-row mb-4">
          <h5 class="text-3xl text-left font-bold flex-1">
            Projected Outcomes by Field
          </h5>
          <a :href="`/customer/` + customerId + `/export-purchases?year=` + filter.year ?? year" target="_blank"
             v-if="this.exportEnabled"
             class="bg-primary px-2 py-1 my-auto rounded tile-shadow border border-blue flex justify-center cursor-pointer hover:bg-grey-lighter">
            <i class="fas fa-download mr-1 my-auto"></i> Export
          </a>
        </div>
        <div class="h-96 overflow-y-scroll">
          <div class="text-center text-xl font-semibold my-40" v-if="this.loadingList">
            Loading...
          </div>
          <table class="rounded-t-lg w-full relative" v-else>
            <thead>
            <tr class="text-left">
              <th class="bg-white rounded-tl sticky top-0 px-4 py-3">Field ID</th>
              <th class="bg-white sticky top-0 px-4 py-3">Year</th>
              <th class="bg-white sticky top-0 px-4 py-3">Size</th>
              <th v-for="(outcomeTypeName,outcomeType) in outcomeTypes"
                  class="bg-white sticky top-0 px-4 py-3">
                {{ outcomeTypeName }} {{ outcomeTypeName === 'CO2e' ? 'Removed & ' : '' }} Reduced<span
                  class="font-normal"></span>
              </th>
              <th class="bg-white sticky top-0 px-4 py-3 text-right">
                Verification Status
              </th>
            </tr>
            </thead>
            <template v-for="purchase in purchases">
              <tr class="border-b border-grey-lighter" v-if="isPurchaseActive(purchase)">
                <td class="px-4 py-3">
                  {{ purchase.swof_id }}
                </td>
                <td class="px-4 py-3">
                  {{ purchase.year }}
                </td>
                <td class="px-4 py-3">
                  {{ purchase.size }} Acres
                </td>
                <template v-for="(outcomeTypeName,outcomeType) in outcomeTypes">
                  <td class="px-4 py-3">
                    {{ roundToTwo(purchase.amounts[2][outcomeType]) }}
                    {{ 'CO2e' === outcomeTypeName ? 'MT' : 'lbs' }}
                  </td>
                </template>
                <td class="px-4 py-3 text-right">
                  {{ purchase.verified ? 'Verified' : 'Not Verified' }}
                </td>
              </tr>
            </template>
            <tfoot>
            <tr class="bg-white font-bold sticky bottom-0" @click="setFieldIndex(-1)" :class="{'bg-green-light': (-1 === fieldIndex)}">
              <td class="px-4 py-3">
                Totals
              </td>
              <td class="px-4 py-3">
              </td>
              <td class="px-4 py-3">
                {{ roundToTwo(this.listTotalSize) }} Acres
              </td>
              <template v-for="(outcomeTypeName,outcomeType) in outcomeTypes">
                <td class="px-4 py-3">
                  {{ roundToTwo(getAmountForSelectedPurchaseType(outcomeType)) }} {{ 'CO2e' === outcomeTypeName ? 'MT' : 'lbs' }}
                </td>
              </template>
              <td class="px-4 py-3 text-right">
                {{ this.allPurchasesVerified }}
              </td>
            </tr>
            </tfoot>
          </table>
        </div>
        <p class="italic">Projected outcomes are not final.
          Final outcomes may be affected by final verification and buffer application.</p>
      </div>


    </div>
    <PurchaseStatusChangeChart
        :customer-id="customerId"
        :customer-types="customerTypes"
        :outcome-types="outcomeTypesWithAcres"
        :purchase-statuses="purchaseStatuses"
        :year="this.filter.year"
        :mapbox-token="mapboxToken"
        :esri-token="esriToken"
        v-show="!this.loading"
        ref="statusChart"
    ></PurchaseStatusChangeChart>
  </div>
</template>
